import cloneDeep from 'lodash/clone';

/**
 * Run workflow graph mutations after Sortable finishes drop cleanup.
 * Connector drops update Vuex immediately today; that re-renders nested
 * draggables while Sortable is still in _onDrop and its root el is null.
 */
export function afterSortableDrop(vm, callback) {
  return new Promise((resolve) => {
    vm.$nextTick(() => {
      setTimeout(() => {
        Promise.resolve(callback()).then(resolve);
      }, 0);
    });
  });
}

export const SUBFLOW_ENTRY_IF = 'entry';
export const SUBFLOW_EXIT_IF = 'exit';

export const getCampaignWorkflowSizeCap = (campaign, usageLimits) => {
  const override = campaign?.workflow_size_limit;
  if (override !== null && override !== undefined && override !== '') {
    const cap = Number(override);
    if (cap > 0) {
      return cap;
    }
  }

  return usageLimits?.workflow_size?.cap ?? null;
};

export const getCampaignWorkflowTriggerCount = (triggers = []) => {
  return triggers.length;
};

export const isSubflowTrigger = ({ type }) => type === 'subflow';

export const isConditionTrigger = ({ type }) => {
  return type === 'conditional' || type === 'wait';
};

export const getGraph = (arr, rootNode) => {
  if (!arr.length) return [];
  const triggers = arr.map((t) => {
    return cloneDeep(t);
  });

  rootNode = rootNode || getRootNode(triggers);
  return rootNode ? [buildNodesGraph(rootNode, triggers)] : [];
};

const getRootNode = (triggers) => {
  const nodesWithoutParent = triggers.filter((i) => {
    return !i.wf_pid && !i.wf_detached;
  });

  nodesWithoutParent.sort((a, b) => {
    const aNestedCount = getNestedCount(a.id, triggers);
    const bNestedCount = getNestedCount(b.id, triggers);
    return aNestedCount > bNestedCount ? -1 : 1;
  });

  return nodesWithoutParent[0];
};

export const getNestedCount = (triggerId, triggers, visited = new Set()) => {
  // Cycle detected, skipping to prevent infinite recursion.
  if (visited.has(triggerId)) {
    return 0;
  }

  visited.add(triggerId);

  const childTriggers = triggers.filter(({ wf_pid, wf_if, id }) => {
    if (wf_pid !== triggerId) {
      return false;
    }

    if (wf_if === SUBFLOW_EXIT_IF) {
      return false;
    }

    const parent = triggers.find((trigger) => trigger.id === triggerId);
    if (parent?.type === 'subflow' && parent.wf_next_trigger === id) {
      return false;
    }

    return true;
  });
  let totalNested = childTriggers.length;

  for (const child of childTriggers) {
    totalNested += getNestedCount(child.id, triggers, visited);
  }

  visited.delete(triggerId); // clean up for other recursion paths

  return totalNested;
};

export const isTriggerInGraph = (trigger, arr) => {
  for (let i of arr) {
    if (i.id === trigger.id) {
      return true;
    }

    if (isTriggerInGraph(trigger, i.nodes || [])) {
      return true;
    }

    if (isTriggerInGraph(trigger, i.afterNodes || [])) {
      return true;
    }
  }

  return false;
};

export const resolveSubflowExitChild = (subflowNode, childNodes) => {
  const exitByKnob = childNodes.find((node) => node.wf_if === SUBFLOW_EXIT_IF);
  if (exitByKnob) {
    return exitByKnob;
  }

  if (!subflowNode?.wf_next_trigger) {
    return null;
  }

  return (
    childNodes.find((node) => node.id === subflowNode.wf_next_trigger) || null
  );
};

export const resolveSubflowEntryChild = (subflowNode, childNodes) => {
  const entryByKnob = childNodes.find(
    (node) => node.wf_if === SUBFLOW_ENTRY_IF
  );
  if (entryByKnob) {
    return entryByKnob;
  }

  const legacyEntry = childNodes.find((node) => {
    return node.wf_if === '' || node.wf_if === null || node.wf_if === undefined;
  });

  return legacyEntry || null;
};

export const stripGraphProps = (trigger) => {
  if (!trigger) {
    return {};
  }

  const {
    nodes,
    afterNodes,
    updating,
    updatingIf,
    jump_hover,
    selected,
    status,
    ...rest
  } = trigger;

  return rest;
};

export const syncSubflowExitPointer = (
  commitUpdateTrigger,
  subflowNode,
  exitTriggerId
) => {
  if (!subflowNode?.id || !isSubflowTrigger(subflowNode)) {
    return;
  }

  const nextPointer = exitTriggerId || '';
  if (subflowNode.wf_next_trigger === nextPointer) {
    return;
  }

  commitUpdateTrigger({ ...subflowNode, wf_next_trigger: nextPointer });
};

export const isInsideSubflowEntryTree = (parentNode, allNodes, knob = null) => {
  if (knob === SUBFLOW_EXIT_IF) {
    return false;
  }

  let current = parentNode;

  while (current?.id) {
    if (current.wf_if === SUBFLOW_EXIT_IF) {
      return false;
    }

    if (current.type === 'subflow') {
      return true;
    }

    if (!current.wf_pid) {
      break;
    }

    current = allNodes.find((node) => node.id === current.wf_pid);
  }

  return false;
};

export const isSubflowInsideEntryTree = (subflowNode, allNodes) =>
  isInsideSubflowEntryTree(
    getSubflowMainlineParent(subflowNode.id, allNodes),
    allNodes,
    subflowNode.wf_if
  );

export const isInsideSubflowEntrySubtree = (triggerId, subflowId, allNodes) => {
  const entryRoot = allNodes.find((node) => {
    return node.wf_pid === subflowId && node.wf_if === SUBFLOW_ENTRY_IF;
  });

  if (!entryRoot || !triggerId) {
    return false;
  }

  let current = allNodes.find((node) => node.id === triggerId);

  while (current?.id) {
    if (current.id === entryRoot.id) {
      return true;
    }

    if (current.id === subflowId) {
      return false;
    }

    if (!current.wf_pid) {
      return false;
    }

    current = allNodes.find((node) => node.id === current.wf_pid);
  }

  return false;
};

export const getSubflowMainlineParent = (subflowId, allNodes) => {
  const subflow = allNodes.find((node) => node.id === subflowId);
  if (!subflow?.wf_pid) {
    return null;
  }

  return allNodes.find((node) => node.id === subflow.wf_pid) || null;
};

/**
 * Before attaching a node to the subflow exit knob, lift the subflow into the
 * moving node's slot so we never create a wf_pid cycle (parent becomes exit child).
 */
export const rewireSubflowBeforeExitAttach = async (
  updateTrigger,
  singleTrigger,
  subflowId,
  movingNode
) => {
  const subflow = singleTrigger(subflowId);
  const parentTrigger = singleTrigger(movingNode.id);

  if (!subflow || !parentTrigger || movingNode.id !== subflow.wf_pid) {
    return;
  }

  await updateTrigger({
    ...stripGraphProps(subflow),
    wf_pid: parentTrigger.wf_pid || '',
    wf_if: parentTrigger.wf_if || '',
  });
};

export const isInsideSubflowExitSubtree = (triggerId, subflowId, allNodes) => {
  const subflow = allNodes.find((node) => node.id === subflowId);
  if (!subflow) {
    return false;
  }

  const childNodes = allNodes.filter((node) => node.wf_pid === subflowId);
  const exitRoot = resolveSubflowExitChild(subflow, childNodes);

  if (!exitRoot || !triggerId) {
    return false;
  }

  let current = allNodes.find((node) => node.id === triggerId);

  while (current?.id) {
    if (current.id === exitRoot.id) {
      return true;
    }

    if (current.id === subflowId) {
      return false;
    }

    if (!current.wf_pid) {
      return false;
    }

    current = allNodes.find((node) => node.id === current.wf_pid);
  }

  return false;
};

export const canAttachToSubflowEntry = (subflowNode, movingNode, allNodes) => {
  if (!isSubflowTrigger(subflowNode) || !movingNode) {
    return false;
  }

  // Tray clones and picker-created nodes are validated in isMovingOperationValid.
  if (!movingNode.id) {
    return true;
  }

  if (movingNode.id === subflowNode.id) {
    return false;
  }

  if (isInsideSubflowExitSubtree(movingNode.id, subflowNode.id, allNodes)) {
    return false;
  }

  const parent = getSubflowMainlineParent(subflowNode.id, allNodes);
  if (!parent) {
    return true;
  }

  if (movingNode.id === parent.id) {
    return true;
  }

  // Block other mainline ancestors — would create a cycle if attached to entry.
  let current = parent;
  while (current?.wf_pid) {
    if (current.id === movingNode.id) {
      return false;
    }
    current = allNodes.find((node) => node.id === current.wf_pid);
  }

  return true;
};

export const canAttachToSubflowExit = (subflowNode, movingNode, allNodes) => {
  if (!isSubflowTrigger(subflowNode) || !movingNode) {
    return false;
  }

  if (
    isSubflowTrigger(movingNode) &&
    isSubflowInsideEntryTree(subflowNode, allNodes)
  ) {
    return false;
  }

  // Tray clones and picker-created nodes are validated in isMovingOperationValid.
  if (!movingNode.id) {
    return true;
  }

  if (movingNode.id === subflowNode.id) {
    return false;
  }

  if (isInsideSubflowEntrySubtree(movingNode.id, subflowNode.id, allNodes)) {
    return false;
  }

  const parent = getSubflowMainlineParent(subflowNode.id, allNodes);
  if (!parent) {
    return true;
  }

  if (movingNode.id === parent.id) {
    return true;
  }

  // Block other mainline ancestors — would create a cycle if attached to exit.
  let current = parent;
  while (current?.wf_pid) {
    if (current.id === movingNode.id) {
      return false;
    }
    current = allNodes.find((node) => node.id === current.wf_pid);
  }

  return true;
};

export const buildNodesGraph = (currentNode, allNodes) => {
  if (!currentNode?.id) return currentNode;

  if (isSubflowTrigger(currentNode)) {
    const childNodes = allNodes.filter(
      (node) => node.wf_pid === currentNode.id
    );
    const entryChild = resolveSubflowEntryChild(currentNode, childNodes);
    const exitChild = resolveSubflowExitChild(currentNode, childNodes);

    currentNode.nodes = entryChild
      ? [buildNodesGraph(entryChild, allNodes)]
      : [];

    currentNode.afterNodes = exitChild
      ? [buildNodesGraph(exitChild, allNodes)]
      : [];

    return currentNode;
  }

  let childNodes = allNodes.filter((node) => {
    return node.wf_pid === currentNode.id && node.wf_if !== SUBFLOW_EXIT_IF;
  });

  if (childNodes.length > 1 && !isConditionTrigger(currentNode)) {
    childNodes = childNodes.slice(0, 1);
  }

  if (isConditionTrigger(currentNode)) {
    childNodes = childNodes.filter((node, index) => {
      const nodeIndex = childNodes.findIndex((n) => {
        return n.wf_if === node.wf_if;
      });
      return index === nodeIndex;
    });
  }

  currentNode.nodes = childNodes.map((node) => {
    return buildNodesGraph(node, allNodes);
  });

  return currentNode;
};
